import { Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Routes } from '../../../../app/routes';
import Table from '../../../../shared/components/table/table';
import { convertServerDateTimeString } from '../../../../shared/utils/format-date/format-date';
import { useSetSearchParamsValue } from '../../../../shared/hooks/use-set-search-params-value';
import AuthGuard from '../../../auth/components/auth-guard/auth-guard';
import styles from '../../../sign-requests/components/sign-requests-trace-logs-table/sign-requests-trace-logs-table.module.css';
import { UserPermissionType } from '../../../user/models/user-permission-type';
import { useGetCustomerTraceLogsQuery } from '../../api/customers-api';
import { useGetUserQuery, useGetUserSettingsQuery } from '../../../user/api/user-api';
import { UserSettings } from '../../../user/models/user-settings';
import { hasPermission } from '../../../../shared/utils/has-permission';

const CustomerTraceLogsTable = (): JSX.Element => {
  const { id } = useParams();
  const { t } = useTranslation('common');
  const [searchParams] = useSearchParams();
  const { data: userSettings } = useGetUserSettingsQuery();
  const { data: currentUser } = useGetUserQuery();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: customerTraceLogs } = useGetCustomerTraceLogsQuery(
    { id: id || '', page: Number(searchParams.get('page')) || 1 },
    {
      skip: !id,
    },
  );

  const navigateToPreviewWithState = (link?: string) => {
    if (link) {
      navigate(link, { state: { from: location } });
    }
  };

  const customersTraceLogsDataSource = useMemo(() => {
    return (customerTraceLogs && Array.isArray(customerTraceLogs.items) ? customerTraceLogs.items : []).map(
      traceLog => ({
        key: traceLog.id,
        user: traceLog.user?.name,
        ipAddress: traceLog.ipAddress,
        customer: traceLog.customer?.name,
        action: traceLog.action,
        link:
          traceLog.signRequest &&
          Routes.SignRequest.replace(':signRequestId', traceLog.signRequest.id).replace(
            ':requestId',
            traceLog.signRequest.request.id,
          ),
        signRequestId: traceLog.signRequest?.id,
        document: traceLog.signRequest?.documents[0]?.id,
        data: traceLog.data ? JSON.stringify(traceLog.data) : '',
        actionAt: convertServerDateTimeString(traceLog.actionAt, userSettings?.[UserSettings.DateFormat]),
      }),
    );
  }, [customerTraceLogs, userSettings]);

  const columns: ColumnsType<(typeof customersTraceLogsDataSource)[number]> = [
    {
      title: t('label.user'),
      dataIndex: 'user',
      key: 'user',
    },
    {
      title: t('label.customer'),
      dataIndex: 'customer',
      key: 'customer',
      hidden: !(currentUser && hasPermission(currentUser, UserPermissionType.ViewCustomers)),
    },
    {
      title: t('label.action'),
      dataIndex: 'action',
      key: 'action',
    },
    {
      title: t('label.time'),
      dataIndex: 'actionAt',
      key: 'actionAt',
    },
    {
      title: t('label.link'),
      dataIndex: 'link',
      width: '20%',
      render: (_, record) =>
        record.link && (
          <div className={styles.linkWrapper}>
            <Typography.Text>{t('label.signRequest')}:</Typography.Text>
            <Typography.Link type="success" onClick={() => navigateToPreviewWithState(record.link)}>
              {record.signRequestId}
            </Typography.Link>
          </div>
        ),
    },
    {
      title: t('label.document'),
      dataIndex: 'document',
      key: 'document',
    },
    {
      title: t('label.ipAddress'),
      dataIndex: 'ipAddress',
      key: 'ipAddress',
    },
    {
      title: t('label.data'),
      dataIndex: 'data',
      key: 'data',
    },
  ];
  const setSearchParamsValue = useSetSearchParamsValue();

  const changePage = (page: number) => setSearchParamsValue('page', page);

  return (
    <AuthGuard permission={UserPermissionType.ViewTracelogs}>
      <Table
        tableProps={{
          pagination: {
            pageSize: 30,
            showQuickJumper: true,
            total: customerTraceLogs?.total,
            current: Number(searchParams.get('page')) || 1,
            onChange: changePage,
            position: ['topRight'],
          },
          dataSource: customersTraceLogsDataSource,
          columns,
        }}
      />
    </AuthGuard>
  );
};
export default CustomerTraceLogsTable;
